import { Link } from 'react-router-dom';
import { Button } from './ui';

/**
 * The deal page's one row of actions (NN0). "Here's the property that's for sale, analyze it,
 * compare it to the comps, give me an investment analysis - but also let me produce an LOI."
 *
 * The heavy four deep-link to the PROPERTY PAGE, which already renders the analyzer, the comps
 * and the valuation - one analyzer screen, never two. The three generators are the deal page's
 * existing dialogs, moved up here from the Deal Room so everything is in one place.
 *
 * Without an analyzer row the heavy four are disabled AND the reason is printed under the row.
 * A silently disabled button reads as broken.
 */
interface Props {
  dealId: string;
  propertyId: string | null;
  onLoi: () => void;
  onProposal: () => void;
  onBrochure: () => void;
}

export default function DealActionStrip({ dealId, propertyId, onLoi, onProposal, onBrochure }: Props) {
  const heavy: { label: string; to: (pid: string) => string }[] = [
    { label: 'Upload Document', to: (pid) => `/upload?property_id=${pid}&deal=${dealId}` },
    { label: 'Analyze', to: (pid) => `/properties/${pid}?deal=${dealId}#analysis` },
    { label: 'Comps', to: (pid) => `/properties/${pid}?deal=${dealId}#comps` },
    { label: 'Run AI Valuation', to: (pid) => `/properties/${pid}?deal=${dealId}#valuation` },
  ];
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        {heavy.map((h) =>
          propertyId ? (
            <Link key={h.label} to={h.to(propertyId)}>
              <Button size="sm" variant="outline">{h.label}</Button>
            </Link>
          ) : (
            <Button key={h.label} size="sm" variant="outline" disabled title="Attach a property first">{h.label}</Button>
          )
        )}
        {/* 🚨 `() => fn()`, NEVER a bare `onClick={fn}`. These props are declared `() => void`, so
            TypeScript ERASES any extra parameter the real handler grew - and the deal page's
            openers each grew an optional `fields` (LOI in the 97th, listing proposal in the 98th,
            brochure in the 99th). Wired bare, every click handed them a React SyntheticEvent as
            their fields, which was then spread into a document prefill. ⛔ The component declares
            a zero-argument contract, so it must CALL them with zero arguments - fixing it here
            protects every caller rather than each call site remembering. */}
        <Button size="sm" variant="outline" onClick={() => onLoi()}>Generate LOI</Button>
        <Button size="sm" variant="outline" onClick={() => onProposal()}>Listing Proposal</Button>
        <Button size="sm" variant="outline" onClick={() => onBrochure()}>Brochure / OM</Button>
      </div>
      {!propertyId && (
        <p className="text-xs text-gray-500 mt-1.5">
          Attach a property to upload, analyze, compare comps or run a valuation.
        </p>
      )}
    </div>
  );
}
